import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@foundation/altzero_base/base/components/ui/card";
import { Button } from "@foundation/altzero_base/base/components/ui/button";
import { Textarea } from "@foundation/altzero_base/base/components/ui/textarea";
import { ArrowLeft, RotateCcw, AlertTriangle, Check, Layout } from "lucide-react";
import { useUser } from "@foundation/altzero_base/base/contextapi/UserContext";
import { useToast } from "@foundation/altzero_base/base/hooks/use-toast";
import ScopingAILayout from "../components/ScopingAILayout";
import {
  getAllTemplates,
  isEditorTemplate,
  extractEditorTemplateSections,
} from "../services/documentLibraryService";
import type { LibraryDocument } from "../types/document-library";
import type { BrandWorkflowConfig } from "../types/brandWorkflowConfig";

const DEFAULT_AI_PROMPT =
  "Please restructure this proposal into a professional format with: a clear title, a table of contents, an executive summary, well-labelled H2 sections for each topic, and a conclusion. Maintain the original meaning and all key details. Apply a professional, confident business tone.";

const TEMPLATE_AI_PROMPT = (sectionNames: string[]) =>
  `Map the content into these sections in this order: ${sectionNames.join(", ")}. Keep all original details and meaning. Apply a professional, confident business tone.`;

const MARKDOWN_PLACEHOLDER = `# Project Title

## Introduction
Brief overview of the project and its goals...

## Scope
Define what is included in this proposal...

## Timeline
Estimated timeline for delivery...

## Budget
Estimated costs and breakdown...`;

interface BrandingData {
  primary_color?: string;
  secondary_color?: string;
  font_family?: string;
  logo_url?: string;
  tagline?: string;
  [key: string]: unknown;
}

interface SelectedTemplate {
  id: string;
  name: string;
  /** Full EditorState pages array (null for markdown templates) */
  pages: any[] | null;
  sectionNames: string[];
}

const BrandYourProposal: React.FC<{ config?: BrandWorkflowConfig }> = ({ config }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { toast } = useToast();

  const LayoutComponent = config?.layout ?? ScopingAILayout;
  const backRoute = config?.routes?.back ?? "/scopingai/new-proposal";
  const generatingRoute = config?.routes?.generating ?? "/scopingai/brand-proposal/generating";
  const sessionDataKey = config?.session?.dataKey ?? "scopingai_brand_proposal_data";
  const titleLabel = config?.labels?.title ?? "Brand Your Proposal";
  const subtitleLabel = config?.labels?.subtitle ?? "Pick a template, paste your Markdown, and let AI brand it for you";
  const generateBtnLabel = config?.labels?.generateButton ?? "Generate Branded Proposal";
  const accent = config?.accentColor ?? "emerald";

  const [markdownContent, setMarkdownContent] = useState("");
  const [aiPrompt, setAiPrompt] = useState(DEFAULT_AI_PROMPT);
  const [brandingData, setBrandingData] = useState<BrandingData | null>(null);
  const [brandingLoading, setBrandingLoading] = useState(true);

  // Template state
  const [templates, setTemplates] = useState<LibraryDocument[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<SelectedTemplate | null>(null);

  // Load branding + templates in parallel
  useEffect(() => {
    if (!user) return;

    const loadBranding = async () => {
      try {
        if (config?.loadBranding) {
          const branding = await config.loadBranding(user);
          setBrandingData(branding as BrandingData | null);
        } else {
          const authResponse = await fetch("/api/scopingai/user/organization", {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": import.meta.env.VITE_API_KEY || "scopingai",
              "x-user-id": user.id,
            },
          });

          if (!authResponse.ok) { setBrandingData(null); return; }

          const orgData = await authResponse.json();
          const organizationId = orgData.data?.organisationId;
          if (!organizationId) { setBrandingData(null); return; }

          const { BrandingService } = await import(
            "@foundation/altzero_base/base/services/brandingService"
          );
          const branding = await BrandingService.getOrganizationBranding(organizationId);
          setBrandingData(branding);
        }
      } catch (err) {
        console.error("Failed to load branding:", err);
        setBrandingData(null);
      } finally {
        setBrandingLoading(false);
      }
    };

    const loadTemplatesFn = config?.loadTemplates ?? getAllTemplates;
    const loadTemplates = async () => {
      try {
        const all = await loadTemplatesFn(user.id);
        setTemplates(all as LibraryDocument[]);
      } catch (err) {
        console.error("Failed to load templates:", err);
      } finally {
        setTemplatesLoading(false);
      }
    };

    loadBranding();
    loadTemplates();
  }, [user]);

  const handleSelectTemplate = (tmpl: LibraryDocument | null) => {
    if (!tmpl) {
      setSelectedTemplate(null);
      setAiPrompt(DEFAULT_AI_PROMPT);
      return;
    }

    const content = tmpl.rawData?.content ?? "";
    let pages: any[] | null = null;
    let sectionNames: string[] = tmpl.sections ?? [];

    if (isEditorTemplate(content)) {
      try {
        pages = JSON.parse(content).pages ?? null;
      } catch { /* ignore */ }
      sectionNames = extractEditorTemplateSections(content);
    }

    const sel: SelectedTemplate = { id: tmpl.id as string, name: tmpl.title, pages, sectionNames };
    setSelectedTemplate(sel);

    if (sectionNames.length > 0) {
      setAiPrompt(TEMPLATE_AI_PROMPT(sectionNames));
    }
  };

  const handleGenerate = () => {
    if (!markdownContent.trim()) return;

    const payload = {
      markdownContent,
      aiPrompt,
      brandingData,
      selectedTemplate: selectedTemplate
        ? {
            id: selectedTemplate.id,
            pages: selectedTemplate.pages,
            sectionNames: selectedTemplate.sectionNames,
          }
        : null,
    };

    sessionStorage.setItem(sessionDataKey, JSON.stringify(payload));
    navigate(generatingRoute);
  };

  const isGenerateDisabled = !markdownContent.trim();

  return (
    <LayoutComponent>
      <div className={`min-h-screen bg-gradient-to-br ${accent === "purple" ? "from-slate-50 via-purple-50 to-indigo-100" : "from-slate-50 via-emerald-50 to-cyan-100"}`}>
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(backRoute)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{titleLabel}</h1>
              <p className="text-sm text-gray-500">{subtitleLabel}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

          {/* Step 1: Choose a Template */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className={`flex items-center justify-center w-7 h-7 rounded-full bg-${accent}-500 text-white text-sm font-bold`}>1</span>
                Choose a Template
              </CardTitle>
            </CardHeader>
            <CardContent>
              {templatesLoading ? (
                <p className="text-sm text-gray-500">Loading templates…</p>
              ) : (
                <div className="space-y-3">
                  {/* No template option */}
                  <button
                    onClick={() => handleSelectTemplate(null)}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                      selectedTemplate === null
                        ? `border-${accent}-500 bg-${accent}-50`
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Layout className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">No template (AI decides structure)</span>
                      </div>
                      {selectedTemplate === null && (
                        <Check className={`h-4 w-4 text-${accent}-500`} />
                      )}
                    </div>
                  </button>

                  {/* Template grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                    {templates.map((tmpl) => {
                      const isSelected = selectedTemplate?.id === (tmpl.id as string);
                      const isDefault = (tmpl.rawData?.metadata as any)?.isDefault;
                      const isEditor = isEditorTemplate(tmpl.rawData?.content ?? "");
                      return (
                        <button
                          key={tmpl.id}
                          onClick={() => handleSelectTemplate(tmpl)}
                          className={`text-left px-4 py-3 rounded-lg border-2 transition-all ${
                            isSelected
                              ? `border-${accent}-500 bg-${accent}-50`
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="text-sm font-medium text-gray-800 truncate">
                                  {tmpl.title}
                                </span>
                                {isDefault && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">Default</span>
                                )}
                                {isEditor && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 shrink-0">Editor</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 line-clamp-1">
                                {tmpl.sections.slice(0, 3).join(" · ")}
                                {tmpl.sections.length > 3 ? ` +${tmpl.sections.length - 3}` : ""}
                              </p>
                            </div>
                            {isSelected && (
                              <Check className={`h-4 w-4 text-${accent}-500 shrink-0 mt-0.5`} />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {selectedTemplate && (
                    <p className={`text-xs text-${accent}-700 bg-${accent}-50 rounded px-3 py-2`}>
                      <strong>{selectedTemplate.name}</strong> selected — AI will map content to:{" "}
                      {selectedTemplate.sectionNames.join(", ")}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Markdown Input */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className={`flex items-center justify-center w-7 h-7 rounded-full bg-${accent}-500 text-white text-sm font-bold`}>2</span>
                Paste Your {config?.labels?.noun ?? "Proposal"} Content
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea
                value={markdownContent}
                onChange={(e) => setMarkdownContent(e.target.value)}
                placeholder={MARKDOWN_PLACEHOLDER}
                className="font-mono resize-y min-h-[300px]"
                rows={16}
              />
              <p className="text-sm text-gray-500 text-right">
                {markdownContent.length} characters
              </p>
            </CardContent>
          </Card>

          {/* Step 3: AI Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className={`flex items-center justify-center w-7 h-7 rounded-full bg-${accent}-500 text-white text-sm font-bold`}>3</span>
                AI Restructuring Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                className="resize-y min-h-[120px]"
                rows={5}
              />
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setAiPrompt(
                      selectedTemplate?.sectionNames.length
                        ? TEMPLATE_AI_PROMPT(selectedTemplate.sectionNames)
                        : DEFAULT_AI_PROMPT
                    )
                  }
                  className="gap-2"
                >
                  <RotateCcw className="h-3 w-3" />
                  Reset to default
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Step 4: Branding Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className={`flex items-center justify-center w-7 h-7 rounded-full bg-${accent}-500 text-white text-sm font-bold`}>4</span>
                Organisation Branding Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {brandingLoading ? (
                <p className="text-sm text-gray-500">Loading branding...</p>
              ) : !brandingData ? (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-yellow-800">
                    No branding configured.{" "}
                    <a href="/settings" className="underline font-medium hover:text-yellow-900">
                      Set up branding in Settings
                    </a>
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {brandingData.logo_url && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-2">Logo</p>
                      <img
                        src={brandingData.logo_url}
                        alt="Organisation logo"
                        className="max-h-16 max-w-[200px] object-contain border border-gray-100 rounded p-1"
                      />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-6">
                    {brandingData.primary_color && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Primary Color</p>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded border border-gray-200 shadow-sm"
                            style={{ backgroundColor: brandingData.primary_color }}
                          />
                          <span className="text-sm text-gray-700 font-mono">{brandingData.primary_color}</span>
                        </div>
                      </div>
                    )}
                    {brandingData.secondary_color && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Secondary Color</p>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded border border-gray-200 shadow-sm"
                            style={{ backgroundColor: brandingData.secondary_color }}
                          />
                          <span className="text-sm text-gray-700 font-mono">{brandingData.secondary_color}</span>
                        </div>
                      </div>
                    )}
                    {brandingData.font_family && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Font Family</p>
                        <span className="text-sm text-gray-700" style={{ fontFamily: brandingData.font_family }}>
                          {brandingData.font_family}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Generate Button */}
          <div className="flex justify-end pb-8">
            <Button
              onClick={handleGenerate}
              disabled={isGenerateDisabled}
              className={`bg-${accent}-500 hover:bg-${accent}-600 text-white px-8 h-12 text-base font-semibold rounded-xl disabled:opacity-50`}
            >
              {generateBtnLabel}
            </Button>
          </div>
        </div>
      </div>
    </LayoutComponent>
  );
};

export default BrandYourProposal;
